import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import PasswordProtection from './components/PasswordProtection';
import sentencesData from "../src/data/sentences_1.json"; // ✅ Import JSON directly
import VideoProvider from "../src/context/VideoContext";
import ScrollingCards from "../src/components/ScrollingCards";
import { useNavigate, Routes, Route } from "react-router-dom"; // Import useNavigate for redirection
import startDevToolsDetection from '../src/services/devToolsDetection';  // Import the detection function
import BookmarksView from './components/BookmarksView';
import _ from "lodash";
import QuotaModal from "../src/components/QuotaModal";
import { UserRequest } from "../src/models/UserRequest.js";
import { quotaStatus } from "../src/services/quotaService.js";
import { checkUserQuota } from "../src/services/apiHelper"; // Import the helper function
import SubscribeButton from '../src/components/SubscribeButton.js';
import SupportUs from '../src/components/SupportUs';
import LocalDateTime from '../src/components/localDateTime';

const WriteFromDictationApp = ({ userEmail }) => {
  const [sentences] = useState(sentencesData.sentences);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state for spinner
  const inputRefs = React.useRef([]); // Refs for input fields
  const navigate = useNavigate(); // Hook for redirection
  const [showSignOut, setShowSignOut] = useState(false); // State for hover effect
  const [isModalOpen, setIsModalOpen] = useState(false); // Set it directly to true

  const getRandomSentences = () => _.sampleSize(sentences, 10);

  const [questions, setQuestions] = useState(getRandomSentences());
  const [userAnswers, setUserAnswers] = useState(Array(10).fill(""));
  const [checked, setChecked] = useState(false);
  const [disabledButtons, setDisabledButtons] = useState(Array(10).fill(false));
  const [enabledInputs, setEnabledInputs] = useState(Array(10).fill(false)); // Initially disable all inputs
  const [checkButtonDisabled, setCheckButtonDisabled] = useState(false); // State for disabling Check Answers button
  const apiCalledRef = useRef(false); // Ref to track API call status

  useEffect(() => {
    if (userEmail && !apiCalledRef.current) {
      apiCalledRef.current = true; // Mark API as called before making the request
      checkUserQuota(userEmail, setLoading, setIsModalOpen);
    }
  }, [userEmail]); // Only re-run when userEmail changes

  useEffect(() => {
    setDisabledButtons(Array(10).fill(false));
    setEnabledInputs(Array(10).fill(false)); // Ensure all inputs are disabled initially
    startDevToolsDetection();  // Start DevTools detection when the app mounts
  }, []);

  const handleSignOut = () => {
    window.location.reload(); // Refresh the page
  };

  const handleSpeak = (sentence, index) => {
    // Enable the corresponding text input
    const updatedInputs = [...enabledInputs];
    updatedInputs[index] = true;
    setEnabledInputs(updatedInputs);

    // Set a short delay to ensure the focus happens immediately after enabling the input
    setTimeout(() => {
      inputRefs.current[index]?.focus(); // Auto-focus the corresponding input box
    }, 0); // Zero delay

    // Select voice (male or female)
    const voices = speechSynthesis.getVoices();

    // Toggle between male and female voices based on index
    // const voice = voices[index % 2 === 0 ? 0 : 1]; // 0 for male, 1 for female (you can adjust the logic based on available voices)

    const utterance = new SpeechSynthesisUtterance(sentence);
    utterance.lang = index % 2 === 0 ? 'en-US' : 'en-GB'; // Randomize male/female voice
    //utterance.voice = voice; // Set the voice to either male or female
    speechSynthesis.speak(utterance);

    // Disable button after speaking
    const updatedButtons = [...disabledButtons];
    updatedButtons[index] = true;
    setDisabledButtons(updatedButtons);
  };

  const handleChange = (index, value) => {
    const newAnswers = [...userAnswers];
    newAnswers[index] = value;
    setUserAnswers(newAnswers);
  };

  const checkAnswers = () => {
    setLoading(true); // Show spinner
    setTimeout(() => {
      setChecked(true);
      setIsChecked(true);
      setCheckButtonDisabled(true); // Disable Check Answers button after clicking
      setLoading(false); // Hide spinner after processing
    }, 1000); // Simulating loading delay
  };

  const loadNewQuestions = () => {
    setLoading(true); // Show spinner
    setTimeout(async () => {
      // Call backend API
      checkUserQuota(userEmail, setLoading, setIsModalOpen);
      setQuestions(getRandomSentences());
      setUserAnswers(Array(10).fill(""));
      setChecked(false);
      setIsChecked(false);
      setDisabledButtons(Array(10).fill(false));
      setEnabledInputs(Array(10).fill(false)); // Reset input enable states
      setCheckButtonDisabled(false); // Enable Check Answers button
      setLoading(false); // Hide spinner after loading
    }, 1000); // Simulating loading delay
  };

  // Check if all answers are filled
  const allAnswered = userAnswers.every(answer => answer.trim() !== "");

  const compareWords = (input, correct) => {
    const inputWords = input.trim().split(/\s+/);  // Split input into words, trim extra spaces
    const correctWords = correct.trim().split(/\s+/);  // Split correct sentence into words

    // Check if the entire input matches the correct sentence (ignores extra spaces)
    const isCorrect = inputWords.join(" ") === correctWords.join(" ");

    return (
      <div className="answer-container">
        {correctWords.map((word, idx) => {
          // Check if the current word matches at the same index
          const wordIsCorrect = inputWords[idx] === word;

          return (
            <span
              key={idx}
              className={`word ${wordIsCorrect ? "correct" : "incorrect"}`}
            >
              {/* Safely display the word or a placeholder if the input doesn't have that word */}
              {inputWords[idx] || "_"}
            </span>
          );
        })}
        {/* Display a status icon to show if the entire input is correct */}
        <span className={`status-icon ${isCorrect ? "correct-icon" : "incorrect-icon"}`}>
          {isCorrect ? "✔️" : "❌"}
        </span>
      </div>
    );
  };

  return (
    <div className="container">
      {/* Profile Icon */}
      <div className="profile-icon-container">
        <div className="profile-icon">
          <img src={process.env.PUBLIC_URL + '/profile-icon.png'} alt="Profile" className="profile-img" />
          <div className="dropdown">
            <button onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </div>

      <div className="scrolling-cards-container">
        <VideoProvider>
          <ScrollingCards />
        </VideoProvider>
      </div>
      <LocalDateTime />
      <h2>PTE Write From Dictation</h2>

      {/* Show Preloader when loading */}
      {loading && (
        <div className="preloader-container">
          <div className="loader"></div>
        </div>
      )}

      <div className="button-container">
        <button onClick={loadNewQuestions} disabled={loading}>Load New Questions</button>
        <SubscribeButton />
      </div>
      <SupportUs /> {/* This will display the message */}
      <p>Click 🔊 to listen to a sentence, then type it exactly as you heard it. Each sentence will be played only once.</p>
      <p>Complete all 10 sentences to activate the <span className="blue-text">Check Answers</span> button at the bottom of the page.</p>

      {questions.map((sentence, index) => (
        <div key={index} style={{ marginBottom: "15px" }}>
          <span className="index-number">{String(index + 1).padStart(2, '0')})</span>
          <button onClick={() => handleSpeak(sentence, index)} disabled={disabledButtons[index]}>
            🔊
          </button>
          <input
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            value={userAnswers[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            disabled={!enabledInputs[index] || isChecked} // Disable input if not enabled or answers are checked
          />
          {checked && (
            <div className="answers-container">
              <strong>Actual: </strong> {sentence}
              <br />
              <strong>Your Answer: </strong> {compareWords(userAnswers[index], sentence)}
            </div>
          )}
        </div>
      ))}

      {/* Show Preloader when checking answers */}
      {loading && (
        <div className="preloader-container">
          <div className="loader"></div>
        </div>
      )}

      <button onClick={checkAnswers} style={{ marginTop: "20px" }} disabled={!allAnswered || checkButtonDisabled || loading}>
        Check Answers
      </button>
      {/* Show modal if locked */}
      {isModalOpen && <QuotaModal isOpen={() => setIsModalOpen(false)} />}
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(''); // State to store logged-in user's email

  const handlePasswordSuccess = (email) => {
    setUserEmail(email);
    setIsAuthenticated(true);
  };

  return (
    <Routes>
      <Route path="/bookmarks-view" element={<BookmarksView />} />
      <Route path="/" element={
        <div>
          {!isAuthenticated ? (
            <PasswordProtection onPasswordSuccess={handlePasswordSuccess} />
          ) : (
            <WriteFromDictationApp userEmail={userEmail} />
          )}
        </div>
      } />
    </Routes>
  );
};

export default App;
